// Overlap sub-view — Story #5305 Part F ("fund-once"), Task #5317 (Stream
// 14a: core UI). Self-contained like SynergyMatrix.tsx/PortfolioAtlas.tsx:
// resolves its own aggregator context and fetches its own data so
// AggregatorPortfolio.tsx only needs to mount it as a tab.
//
// Design §6.2: "the first run starts itself" — auto-first-run is entirely
// server-side (handler.py's _get_portfolio_overlap auto-enqueues when no
// run record exists at all), so this view just polls while status is
// 'processing' and never triggers a compute on mount itself; "Re-run" below
// is the one deliberate re-run action this stream owns.
//
// Lifecycle actions (setting a group's status, the fund-once workbench) and
// the funding-mentions rollup (OverlapFundingCard, Task #5321's P1 review
// fix — see ADO Bug #5338) are now live. A dismissed-groups review list is
// still NOT in the real served payload — see the DATA GAPS note on
// OverlapGroup/OverlapWeakPair in types/whitespace.ts. This view renders
// exactly what handler.py's routes return and is honest about the rest
// rather than fabricating it.
import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Layers, RefreshCw, ChevronDown } from 'lucide-react'
import {
  getPortfolioOverlap,
  getPortfolioOverlapGroup,
  getPortfolioMap,
  getAggregatorPortfolioProjects,
  getPortfolioSynergy,
  computePortfolioOverlap,
} from '../../services/api'
import { formatComputedAgo, synergyCellsForGroupMembers, OVERLAP_STALE_GROUP_MESSAGE, portfolioProjectNames } from '../../lib/whitespace'
import type { OverlapRunStage } from '../../types/whitespace'
import type { AggregatorProject } from '../../types'
import { useAggregator } from '../../contexts/AggregatorContext'
import GroupCard from './GroupCard'
import GroupDetail from './GroupDetail'
import OverlapFundingCard from './OverlapFundingCard'
import LoadingSkeleton from '../shared/LoadingSkeleton'
import EmptyState from '../shared/EmptyState'
import ScanStatusChip from '../ui/ScanStatusChip'
import HelpPopover from '../help/HelpPopover'
import HowToReadThisCard from '../help/HowToReadThisCard'

// handler.py's real OVERLAP_RUN_STAGE_* strings, in server order — this
// stream's first consumer of the shared ScanStatusChip (design §5.2).
const OVERLAP_RUN_STAGES: readonly OverlapRunStage[] = [
  'fetching documents',
  'comparing projects',
  'forming groups',
  'ready',
]

interface OverlapViewProps {
  /** Seeds the selected group on mount — the Atlas Overlap lens and Synergy
   * Matrix's cross-links switch to this tab and pass the group id they
   * clicked through from, so the group detail opens directly rather than
   * landing on "select a group". Ignored after the first render (the tab
   * bar remounts this component on every switch, so this only ever needs
   * to seed initial state once). */
  initialGroupId?: string | null
  /** design §6.3 "cross-links both ways" — Synergy Matrix already links
   * INTO a group here (via onOpenOverlapGroup); this callback is the return
   * link, switching AggregatorPortfolio to the Synergy tab. Omitted when
   * unavailable rather than a dead button. */
  onOpenSynergy?: () => void
  /** Bump this (e.g. Date.now()) to ask the view to select its first group
   * once groups are loaded — used by the "Reading your portfolio" guided
   * tour's fund-once step so it has a real group to spotlight. A no-op when
   * a group is already selected or none exists, or when initialGroupId
   * already seeded a selection. */
  autoSelectSignal?: number
}

export default function OverlapView({ initialGroupId = null, onOpenSynergy, autoSelectSignal }: OverlapViewProps) {
  const { activeAggregatorId } = useAggregator()
  const qc = useQueryClient()
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(initialGroupId)
  const [showWeakSignals, setShowWeakSignals] = useState(false)
  const [isReRunning, setIsReRunning] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const overlapQueryKey = ['portfolio-overlap', activeAggregatorId]
  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: overlapQueryKey,
    queryFn: () => getPortfolioOverlap(activeAggregatorId),
    refetchInterval: (query) => (query.state.data?.status === 'processing' ? 3000 : false),
  })

  // Shared with the Portfolio Atlas's own query key so React Query dedupes
  // the fetch — the mini-map reuses that SAME layout, never a second call.
  const { data: mapData } = useQuery({
    queryKey: ['portfolio-map', activeAggregatorId],
    queryFn: () => getPortfolioMap(activeAggregatorId),
  })

  // Real project names/entities/document counts for member chips — the
  // SAME query AggregatorPortfolio.tsx's own KPI tiles already fetch, so
  // this is a dedupe, not a new request.
  const { data: portfolioData } = useQuery({
    queryKey: ['aggregator-portfolio', activeAggregatorId],
    queryFn: () => getAggregatorPortfolioProjects(activeAggregatorId),
  })
  const projectNames = portfolioProjectNames(portfolioData)
  const projectsById = new Map<string, AggregatorProject>((portfolioData?.projects ?? []).map((p) => [p.project_id, p]))
  // Overlay name-only entries for entitled members not in the grant-scoped
  // `projects` list, so member labels resolve instead of showing a raw UUID
  // (Story #5305 CoWork re-test). entity_name/document_count stay absent for
  // these and are guarded at the GroupDetail render site.
  for (const [id, name] of projectNames) {
    if (!projectsById.has(id)) projectsById.set(id, { project_id: id, name } as AggregatorProject)
  }

  const groupDetailQuery = useQuery({
    queryKey: ['portfolio-overlap-group', activeAggregatorId, selectedGroupId],
    queryFn: () => getPortfolioOverlapGroup(selectedGroupId as string, activeAggregatorId),
    enabled: !!selectedGroupId,
    retry: false,
  })

  // Auto-select the first group on request (guided tour only — see the prop
  // doc above). Ignores signal 0/undefined so mounting never auto-selects;
  // re-checks whenever data arrives in case the request came in before the
  // groups had loaded. Never overrides initialGroupId's own seeded selection.
  useEffect(() => {
    if (!autoSelectSignal) return
    if (selectedGroupId) return
    const first = data?.groups?.[0]
    if (first) setSelectedGroupId(first.group_id)
  }, [autoSelectSignal, data, selectedGroupId])

  // Shares SynergyMatrix's exact query key — the reconciliation cross-link
  // (design §6.3) costs no extra fetch when both tabs have been visited.
  const synergyQuery = useQuery({
    queryKey: ['portfolio-synergy', activeAggregatorId],
    queryFn: () => getPortfolioSynergy(activeAggregatorId),
  })
  const reconciledSynergyCells = groupDetailQuery.data
    ? synergyCellsForGroupMembers(synergyQuery.data?.cells ?? [], groupDetailQuery.data.member_project_ids)
    : []

  const handleReRun = async () => {
    setIsReRunning(true)
    try {
      const run = await computePortfolioOverlap(activeAggregatorId)
      if (run.cooldown_remaining_seconds != null) {
        const mins = Math.ceil(run.cooldown_remaining_seconds / 60)
        setNotice(`Nothing has changed since the last run yet — try again in about ${mins} min.`)
      } else {
        setNotice('Re-run started — this can take a few minutes for a large portfolio.')
      }
      qc.invalidateQueries({ queryKey: overlapQueryKey })
    } catch (e) {
      setNotice((e as Error)?.message ?? 'Failed to start a re-run.')
    } finally {
      setIsReRunning(false)
    }
  }

  if (isLoading) {
    return (
      <div data-testid="overlap-view-loading">
        <LoadingSkeleton type="card" rows={3} />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="p-4 text-sm text-red-600" role="alert" data-testid="overlap-view-error">
        Couldn't load the Overlap view: {(error as Error)?.message ?? 'unknown error'}
      </div>
    )
  }

  // Never a blank tab (design §6.2/AC1) — a run in flight always gets
  // staged progress, never an empty grid.
  if (data && data.status === 'processing') {
    return (
      <div data-testid="overlap-view-computing" className="flex flex-col items-center justify-center py-16 px-4 text-center">
        <RefreshCw className="w-7 h-7 text-iflow-500 animate-spin mb-3" />
        <h3 className="text-base font-semibold text-gray-800 mb-1">Comparing your portfolio for possible duplicated effort</h3>
        <div className="mt-1">
          <ScanStatusChip
            stage={data.stage}
            stages={OVERLAP_RUN_STAGES}
            startedAt={data.started_at}
            variant={data.run_type === 'manual' ? 'updating' : 'computing'}
          />
        </div>
        <p className="text-sm text-gray-600 max-w-sm mt-2">
          This runs in the background; you can leave this tab and come back.
        </p>
      </div>
    )
  }

  if (data && data.status === 'failed') {
    return (
      <div className="p-4 text-sm text-red-600" role="alert" data-testid="overlap-view-failed">
        The last overlap compute failed. Try Re-run below, or come back later.
        <div className="mt-2">
          <button onClick={handleReRun} disabled={isReRunning} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-red-200 text-red-700 hover:bg-red-50 rounded-lg">
            <RefreshCw className={`w-3.5 h-3.5 ${isReRunning ? 'animate-spin' : ''}`} /> Re-run
          </button>
        </div>
      </div>
    )
  }

  const groups = data?.groups ?? []
  const weakPairs = data?.weak_pairs ?? []
  const groupNotFound = !!selectedGroupId && groupDetailQuery.isError

  return (
    <div data-testid="overlap-view">
      <HowToReadThisCard
        visualisationId="overlap-view"
        title="Overlap groups"
        body="Each card is a group of projects your scan judged to be working the same ground — not just similar, but sharing real matched documents. The score under a card is the strongest pair inside it, and it's a share of documents matched, never a similarity percentage."
        example={
          'A card headed "Substantially the same work" with a strongest pair of 93 means the smaller project\'s ' +
          'documents matched documents in the other project 93% of the time — a strong consolidation candidate, not a coincidence.'
        }
      />
      <div className="flex gap-3.5">
      <div className="w-[380px] flex-shrink-0 flex flex-col gap-2.5 overflow-y-auto max-h-[calc(100vh-260px)]" data-tour="overlap-group-list">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-iflow-900 flex items-center gap-1.5">
            <Layers className="w-4 h-4" /> Possible duplicated effort
          </h2>
          <HelpPopover topic="pf-overlap" size={14} />
          <span className="flex-1" />
          <span className="text-[11px] text-gray-500">
            {formatComputedAgo(data?.completed_at ?? null)} ·{' '}
            <button onClick={handleReRun} disabled={isReRunning} className="text-iflow-700 hover:text-iflow-800 font-semibold hover:underline">
              {isReRunning ? 'Starting…' : 'Re-run'}
            </button>
          </span>
        </div>
        <p className="text-[11px] text-gray-600 leading-relaxed">
          Groups of projects working the same ground. Each pair's score is the share of the smaller project's
          comparable documents with a close counterpart in the other.
        </p>

        {notice && (
          <div className="p-2 bg-sky-50 border border-sky-200 text-iflow-700 text-xs rounded-lg">{notice}</div>
        )}

        {groups.length === 0 ? (
          <EmptyState
            icon={<Layers className="w-7 h-7" />}
            title="No overlap groups found"
            description="No two or more of your portfolio's projects currently score as working the same ground."
          />
        ) : (
          groups
            .slice()
            .sort((a, b) => b.score - a.score)
            .map((group) => (
              <GroupCard
                key={group.group_id}
                group={group}
                projectNames={projectNames}
                selected={group.group_id === selectedGroupId}
                onSelect={() => setSelectedGroupId(group.group_id)}
              />
            ))
        )}

        {weakPairs.length > 0 && (
          <div className="card p-2.5 border-dashed">
            <p className="text-[11px] leading-relaxed">
              <b className="text-iflow-900">{weakPairs.length} weak signal{weakPairs.length === 1 ? '' : 's'}</b> (score
              25–39) — shown only when you look, never notified.{' '}
              <button onClick={() => setShowWeakSignals((v) => !v)} className="text-iflow-700 hover:text-iflow-800 font-semibold hover:underline">
                {showWeakSignals ? 'Hide' : 'Show'}
              </button>
            </p>
            {showWeakSignals && (
              <ul className="mt-1.5 space-y-1" data-testid="overlap-weak-signals">
                {weakPairs.map((p, i) => (
                  <li key={i} className="text-[10.5px] text-gray-600">
                    {projectNames.get(p.project_a_id) ?? p.project_a_id} ×{' '}
                    {projectNames.get(p.project_b_id) ?? p.project_b_id} — score {p.coefficient}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <OverlapFundingCard onOpenGroup={(groupId) => setSelectedGroupId(groupId)} />

        <div className="card p-2.5" data-testid="overlap-rollups-unavailable">
          <p className="text-[11px] text-gray-400 italic leading-relaxed">
            A dismissed-groups review list is not in this view yet — open a group's detail for its lifecycle status,
            note and the fund-once workbench, all of which are live.
          </p>
        </div>
      </div>

      <div className="flex-1 min-w-0">
        {!selectedGroupId && (
          <EmptyState
            icon={<ChevronDown className="w-7 h-7" />}
            title="Select a group"
            description="Pick a group on the left to see its members and footprint."
          />
        )}
        {selectedGroupId && groupNotFound && (
          <div className="p-4 text-sm text-gray-600" data-testid="overlap-group-unavailable">
            {OVERLAP_STALE_GROUP_MESSAGE}
          </div>
        )}
        {selectedGroupId && groupDetailQuery.isLoading && (
          <div data-testid="overlap-group-detail-loading">
            <LoadingSkeleton type="card" rows={2} />
          </div>
        )}
        {selectedGroupId && groupDetailQuery.data && (
          <>
            <GroupDetail
              group={groupDetailQuery.data}
              projectsById={projectsById}
              layout={mapData?.layout ?? null}
              aggregatorId={activeAggregatorId}
            />
            {reconciledSynergyCells.length > 0 && (
              <div className="card p-2.5 mt-2.5" data-testid="overlap-synergy-reconciliation">
                <p className="text-[11px] text-gray-700 leading-relaxed">
                  <b className="text-iflow-900">
                    {reconciledSynergyCells.length} synergy cell{reconciledSynergyCells.length === 1 ? '' : 's'}
                  </b>{' '}
                  also link{reconciledSynergyCells.length === 1 ? 's' : ''} these projects in the Synergy Matrix.
                  {onOpenSynergy && (
                    <button onClick={onOpenSynergy} className="ml-1.5 text-iflow-700 hover:text-iflow-800 font-semibold hover:underline">
                      Open Synergy Matrix →
                    </button>
                  )}
                </p>
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  )
}
